import { cycleInfo, periodDay, phaseOf } from "./cycle";
import { cycleGate } from "./cycleCare";
import { addDays } from "./date";
import { hasCondition } from "./eating";
import { doseIndex, sessionKeyFor } from "./plan";
import type { TrainingDevice } from "./programme";
import { sessionMinutes } from "./queue";
import { choice, symptomsOf } from "./symptoms";
import { isSteadyKey } from "./templates";
import type { KayaState, SessionKey } from "./types";
import { gentleOffKey, plannedKey } from "./week";

/**
 * The cycle changes a session by symptom, never by phase (ADR-057).
 *
 * One sentence for the day's session, or null — which is the answer on most days. It states the
 * rule and its reason, offers and never orders, and the normal session stays one tap away. The
 * rules are cycle-and-training.md §4.3–§4.5: what she logged today changes the offer; a period
 * day keeps the existing swap to the gentle session but carries "Keep my session"; the week before
 * a period and a hot day after ovulation each get one line, and only in a natural, regular cycle.
 * Nothing here is persisted: "Keep my session" and a taken offer are the day's override, through
 * the tombstoned writer (ADR-019).
 */

export type CycleSuggestionKind = "period" | "symptom" | "pms" | "heat" | "pregnant";

export interface CycleOffer {
  key: SessionKey;
  /** "Gentle session", "Short version" — the screen adds the minutes */
  label: string;
  /** "swap": the day's override; "short": the day's own Steady session run short (ADR-052) */
  how: "swap" | "short";
}

export interface CycleSuggestion {
  kind: CycleSuggestionKind;
  /** the one sentence: the rule and its reason */
  line: string;
  /** a short cite, for the tests and the ADR — shown nowhere as a grade */
  evidence: string;
  /** the change offered beside Start; absent when the line is only a note */
  offer?: CycleOffer;
  /** a period day's swap is in place: "Keep my session" puts the weekday's session back */
  keep?: boolean;
  /** what the line is about, for the Change today sheet: "period day 2", "low energy logged" */
  reason: string;
  /** the "see a GP" sentence is part of the line */
  gp?: boolean;
}

export const PREGNANT_LINE =
  "In pregnancy, keep moving if your doctor or midwife agrees; avoid lying flat for long, overheating and falls.";
export const HEAT_LINE = "Hot day, and your temperature runs a little higher after ovulation: take water to the session.";
export const PMS_LINE =
  "If you feel flat this week, a shorter session still counts — exercise eases premenstrual symptoms (Pearce 2020).";

const GP_PAIN = "Severe period pain that keeps coming back is worth a GP visit.";
const GP_SOAKED = "Soaking through in under 2 hours more than once is worth a GP visit.";
const GP_BOTH = "Severe pain and soaking through that keep coming back are worth a GP visit.";

/** How far back "keeps coming back" looks: four cycles and a little. */
const GP_LOOKBACK = 120;

/** The sessions with fast work in them: the ones a "no intervals" rule moves off. */
const INTERVALS: readonly SessionKey[] = ["B"];
/** Kaya's own sessions whose short version is the 15-minute floor session. */
const SHORT_TO_T: readonly SessionKey[] = ["A", "B", "C"];

interface Logged {
  pain: number | null;
  heavy: boolean;
  soaked: boolean;
  lowEnergy: boolean;
  poorSleep: boolean;
  headache: boolean;
}

function logged(S: KayaState, ds: string): Logged {
  const keys = symptomsOf(S, ds);
  const p = choice(keys, "pain");
  return {
    // the old "cramps" chip said there was pain but not how much; the symptom map reads it as 2
    pain: p !== null ? Number(p) : keys.includes("cramps") ? 2 : null,
    heavy: choice(keys, "flow") === "heavy",
    soaked: keys.includes("soaked"),
    lowEnergy: choice(keys, "energy") === "0",
    poorSleep: choice(keys, "sleep") === "0",
    headache: keys.includes("headache"),
  };
}

/** What she logged today, in the words the Cycle row uses: "cramps, low energy". */
export function loggedWords(S: KayaState, ds: string): string[] {
  const l = logged(S, ds);
  const out: string[] = [];
  if (l.pain !== null && l.pain > 0) out.push(l.pain === 3 ? "severe cramps" : "cramps");
  if (l.soaked) out.push("soaking through");
  else if (l.heavy) out.push("heavy flow");
  if (l.lowEnergy) out.push("low energy");
  if (l.poorSleep) out.push("poor sleep");
  if (l.headache) out.push("headache");
  return out;
}

/** Days in the lookback, `ds` included, on which `test` held. */
function daysWith(S: KayaState, ds: string, test: (l: Logged) => boolean): string[] {
  const out: string[] = [];
  for (let i = 0; i < GP_LOOKBACK; i++) {
    const d = addDays(ds, -i);
    if (S.logs[d]?.symptoms && test(logged(S, d))) out.push(d);
  }
  return out;
}

/**
 * The "see a GP" sentence, or null: severe pain logged today and in an earlier period (NICE NG73),
 * or soaking through logged today and on another day (NICE NG88). One sentence even when both
 * apply.
 */
export function gpLine(S: KayaState, ds: string): string | null {
  const today = logged(S, ds);
  const periodOf = (d: string) => cycleInfo(S, d)?.last ?? d;
  const pain =
    today.pain === 3 && new Set(daysWith(S, ds, (l) => l.pain === 3).map(periodOf)).size >= 2;
  const soaked = today.soaked && daysWith(S, ds, (l) => l.soaked).length >= 2;
  return pain && soaked ? GP_BOTH : pain ? GP_PAIN : soaked ? GP_SOAKED : null;
}

/** The shorter session for `key`, or null when there is none to offer. */
function shortOffer(key: SessionKey): CycleOffer | null {
  if (isSteadyKey(key)) return { key, label: "Short version", how: "short" };
  if (SHORT_TO_T.includes(key)) return { key: "T", label: "Short version", how: "swap" };
  return null;
}

const GENTLE: CycleOffer = { key: "P", label: "Gentle session", how: "swap" };

interface Rule {
  line: string;
  reason: string;
  evidence: string;
  offer: CycleOffer | null;
  /** a line that keeps the session and only says so (PCOS); any other rule needs an offer */
  note?: boolean;
}

/**
 * The symptom table (cycle-and-training.md §4.3), gentlest first: the first rule whose offer
 * changes the day wins, so the offer is the gentlest any logged symptom asks for. With PCOS a
 * rule that would only take the intervals away keeps them and says so (Patten 2020; the 2023
 * guideline) — a pain or flow rule still offers the gentle session, because that is about the day.
 */
function symptomRule(S: KayaState, ds: string, key: SessionKey): Rule | null {
  const l = logged(S, ds);
  const fast = INTERVALS.includes(key);
  const pcos = hasCondition(S, "pcos");
  const short = shortOffer(key);
  const rules: Rule[] = [];

  if (l.pain === 3)
    rules.push({
      line: "You logged severe pain: the gentle session is offered, or rest. If moving helps, your session is fine too.",
      reason: "severe pain logged",
      evidence: "Armour 2019 (Cochrane); NICE NG73",
      offer: GENTLE,
    });
  if (l.pain === 2)
    rules.push({
      line: "You logged moderate pain: the gentle session is offered — movement usually eases cramps (Cochrane 2019).",
      reason: "moderate pain logged",
      evidence: "Armour 2019 (Cochrane)",
      offer: GENTLE,
    });
  if (l.heavy || l.soaked)
    rules.push({
      line: "You logged heavy flow: the gentle session is offered, with no long planks.",
      reason: "heavy flow logged",
      evidence: "Bruinvels 2016; NICE NG88",
      offer: GENTLE,
    });
  if (l.pain === 1)
    rules.push({
      line: "You logged mild pain: moving usually eases it (Cochrane 2019), so your session is fine; the gentle one is here if you want it.",
      reason: "mild pain logged",
      evidence: "Armour 2019 (Cochrane)",
      offer: GENTLE,
    });

  const tired = l.lowEnergy && l.poorSleep ? "low energy and poor sleep" : l.lowEnergy ? "low energy" : l.poorSleep ? "poor sleep" : null;
  if (l.headache && fast)
    rules.push(
      pcos
        ? {
            line: "You logged a headache: stop after any round if it gets worse — a short session still counts.",
            reason: "a headache logged",
            evidence: "comfort, not evidence; PCOS guideline 2023",
            offer: null,
            note: true,
          }
        : {
            line: "You logged a headache: a shorter session without the intervals is offered — for comfort, not a rule.",
            reason: "a headache logged",
            evidence: "comfort, not evidence",
            offer: short,
          }
    );
  if (tired) {
    const both = l.lowEnergy && l.poorSleep;
    rules.push(
      pcos && fast
        ? {
            line: `You logged ${tired}: stop after any round if you need to — a short session still counts.`,
            reason: `${tired} logged`,
            evidence: "Zhang 2021; Patten 2020; PCOS guideline 2023",
            offer: null,
            note: true,
          }
        : {
            line: `You logged ${tired}: a shorter session${both && fast ? " without the intervals" : ""} is offered. A short session still counts.`,
            reason: `${tired} logged`,
            evidence: "Zhang 2021 (autoregulation); Bruinvels 2021",
            offer: short,
          }
    );
  }

  // An offer that names the session already running changes nothing — unless it runs it short.
  return rules.find((r) => r.note || (r.offer && (r.offer.key !== key || r.offer.how === "short"))) ?? null;
}

function withGp(line: string, gp: string | null): string {
  return gp ? `${line} ${gp}` : line;
}

/**
 * The day's cycle line for the session, or null. `opts.hot` stands in for the day's hot switch
 * (`DayLog.hot`), for a screen that already holds it.
 */
export function cycleSuggestion(S: KayaState, ds: string, opts: { hot?: boolean } = {}): CycleSuggestion | null {
  const gate = cycleGate(S, ds);
  if (gate === "off") return null;

  const key = sessionKeyFor(S, ds);
  if (key === "R") return null;
  if (gate === "pregnant")
    return { kind: "pregnant", line: PREGNANT_LINE, evidence: "ACOG Committee Opinion 804 (2020)", reason: "pregnancy" };

  const L = S.logs[ds];
  // Once the session has begun, swapping it would orphan its ticks: the offer is for before Start.
  const begun = !!L?.ran;
  const chosen = !!S.overrides[ds];
  const gp = gpLine(S, ds);

  if (!begun && !chosen) {
    const day = cycleInfo(S, ds)?.day ?? null;
    if (key === "P" && plannedKey(S, ds) === "P" && periodDay(S, ds) && gentleOffKey(S, ds)) {
      const n = day === null ? "" : ` ${day}`;
      return {
        kind: "period",
        line: withGp(
          `Period day${n}: the gentle session is offered — movement eases cramps (Cochrane 2019). Keeping your normal session is fine.`,
          gp
        ),
        evidence: "Armour 2019 (Cochrane); McNulty 2020",
        keep: true,
        reason: `period day${n}`,
        ...(gp ? { gp: true } : {}),
      };
    }
    const r = symptomRule(S, ds, key);
    if (r)
      return {
        kind: "symptom",
        line: withGp(r.line, gp),
        evidence: r.evidence,
        ...(r.offer ? { offer: r.offer } : {}),
        reason: r.reason,
        ...(gp ? { gp: true } : {}),
      };
  } else if (gp) {
    // her own choice stands, but the GP sentence is worth saying anyway
    return { kind: "symptom", line: gp, evidence: "NICE NG73; NICE NG88", reason: "logged today", gp: true };
  }

  if (gate !== "phases") return null;
  const hot = opts.hot ?? !!L?.hot;
  if (hot && phaseOf(S, ds) === "luteal")
    return { kind: "heat", line: HEAT_LINE, evidence: "Giersch 2020", reason: "a hot day" };
  if (cycleInfo(S, ds)?.pms) {
    const short = !begun && !chosen ? shortOffer(key) : null;
    return {
      kind: "pms",
      line: PMS_LINE,
      evidence: "Pearce 2020; Bruinvels 2021",
      ...(short ? { offer: short } : {}),
      reason: "the week before your period",
    };
  }
  return null;
}

/** The session "Keep my session" puts back on a period day, or null when there is none. */
export function keptKey(S: KayaState, ds: string): SessionKey | null {
  return gentleOffKey(S, ds);
}

/** How long the offered session runs, for its button: "Short version · 18 min". */
export function offerMinutes(S: KayaState, offer: CycleOffer, device?: TrainingDevice): number {
  return sessionMinutes(S, offer.key, doseIndex(S, offer.key), offer.how === "short" ? { device, short: true } : { device });
}
